import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Building2, UserCheck, Shield, CheckCircle, 
  Mail, Phone, MapPin, Globe, ArrowRight 
} from 'lucide-react';
import { mockStates, treatmentTypes, specializations, insuranceProviders } from '../data/mock';
import { apiCall } from '../utils/api';

const FacilitySignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Account Information
    email: '',
    password: '',
    confirmPassword: '',
    
    // Facility Basic Information
    facilityName: '',
    contactPerson: '',
    title: '',
    phone: '',
    website: '',
    
    // Location Information
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Facility Details
    description: '',
    established: '',
    treatmentTypes: [],
    specializations: [],
    insurance: [],
    capacity: '',
    accreditation: '',
    
    // Agreement
    agreeToTerms: false,
    agreeToReview: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleArrayChange = (field, item, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], item]
        : prev[field].filter(i => i !== item)
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (formData.password && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }
    
    if (stepNum === 2) {
      if (!formData.facilityName) newErrors.facilityName = 'Facility name is required';
      if (!formData.contactPerson) newErrors.contactPerson = 'Contact person is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    }
    
    if (stepNum === 3) {
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    }
    
    if (stepNum === 4) {
      if (!formData.description) newErrors.description = 'Description is required';
      if (formData.treatmentTypes.length === 0) {
        newErrors.treatmentTypes = 'Select at least one treatment type';
      }
    }
    
    if (stepNum === 5) {
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to terms';
      if (!formData.agreeToReview) newErrors.agreeToReview = 'You must agree to review process';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await apiCall('/api/facility/register', {
        method: 'POST',
        body: JSON.stringify({
          user_data: {
            email: formData.email,
            password: formData.password,
          },
          submission_data: {
            facility_name: formData.facilityName,
            contact_person: formData.contactPerson,
            title: formData.title || null,
            phone: formData.phone,
            website: formData.website || null,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zipCode,
            description: formData.description,
            established: formData.established || null,
            capacity: formData.capacity || null,
            accreditation: formData.accreditation || null,
            treatment_types: formData.treatmentTypes,
            specializations: formData.specializations,
            insurance: formData.insurance,
            user_email: formData.email,
          },
        }),
      });

      navigate('/facility-signup/success');
    } catch (error) {
      setSubmitError(
        error.message?.includes('400')
          ? 'This email is already registered. Try signing in instead.'
          : 'Something went wrong submitting your registration. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Account', icon: <UserCheck className="h-5 w-5" /> },
    { number: 2, title: 'Contact', icon: <Phone className="h-5 w-5" /> },
    { number: 3, title: 'Location', icon: <MapPin className="h-5 w-5" /> },
    { number: 4, title: 'Details', icon: <Building2 className="h-5 w-5" /> },
    { number: 5, title: 'Review', icon: <CheckCircle className="h-5 w-5" /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Join Addiction Rehab Call Directory
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              List your addiction treatment facility for free and connect with people seeking recovery. 
              All submissions are reviewed to ensure quality and accuracy.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {steps.map((stepItem, index) => (
                <div key={stepItem.number} className="flex flex-col items-center">
                  <div className={`
                    flex items-center justify-center w-12 h-12 rounded-full border-2 mb-2
                    ${step >= stepItem.number 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-400'
                    }
                  `}>
                    {stepItem.icon}
                  </div>
                  <span className={`text-sm font-medium ${
                    step >= stepItem.number ? 'text-blue-600' : 'text-gray-400'
                  }`}>
                    {stepItem.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`absolute h-0.5 w-16 ml-12 mt-6 ${
                      step > stepItem.number ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                {steps[step - 1].icon}
                <span className="ml-2">Step {step}: {steps[step - 1].title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Account Information */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@facility-email.com"
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Minimum 8 characters"
                        className={errors.password ? 'border-red-500' : ''}
                      />
                      {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        placeholder="Confirm your password"
                        className={errors.confirmPassword ? 'border-red-500' : ''}
                      />
                      {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                )}

                {/* Step 2: Contact Information */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="facilityName">Facility Name *</Label>
                      <Input
                        id="facilityName"
                        value={formData.facilityName}
                        onChange={(e) => handleInputChange('facilityName', e.target.value)}
                        placeholder="e.g., Sunrise Recovery Center"
                        className={errors.facilityName ? 'border-red-500' : ''}
                      />
                      {errors.facilityName && <p className="text-red-500 text-sm mt-1">{errors.facilityName}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactPerson">Contact Person *</Label>
                        <Input
                          id="contactPerson"
                          value={formData.contactPerson}
                          onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                          placeholder="Full name"
                          className={errors.contactPerson ? 'border-red-500' : ''}
                        />
                        {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
                      </div>

                      <div>
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          placeholder="e.g., Director, Administrator"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className={errors.phone ? 'border-red-500' : ''}
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>

                      <div>
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          type="url"
                          value={formData.website}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          placeholder="https://www.yourfacility.com"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Location Information */}
                {step === 3 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="address">Street Address *</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="123 Recovery Lane"
                        className={errors.address ? 'border-red-500' : ''}
                      />
                      {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          placeholder="City name"
                          className={errors.city ? 'border-red-500' : ''}
                        />
                        {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                      </div>

                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
                          <SelectTrigger className={errors.state ? 'border-red-500' : ''}>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {mockStates.map((state) => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                      </div>

                      <div>
                        <Label htmlFor="zipCode">ZIP Code *</Label>
                        <Input
                          id="zipCode"
                          value={formData.zipCode}
                          onChange={(e) => handleInputChange('zipCode', e.target.value)}
                          placeholder="12345"
                          className={errors.zipCode ? 'border-red-500' : ''}
                        />
                        {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Facility Details */}
                {step === 4 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="description">Facility Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Describe your facility, treatment approach, and what makes you unique..."
                        rows={4}
                        className={errors.description ? 'border-red-500' : ''}
                      />
                      {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="established">Year Established</Label>
                        <Input
                          id="established"
                          value={formData.established}
                          onChange={(e) => handleInputChange('established', e.target.value)}
                          placeholder="e.g., 1995"
                        />
                      </div>

                      <div>
                        <Label htmlFor="capacity">Patient Capacity</Label>
                        <Input
                          id="capacity"
                          value={formData.capacity}
                          onChange={(e) => handleInputChange('capacity', e.target.value)}
                          placeholder="e.g., 50 patients"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Treatment Types Offered *</Label>
                      <div className="grid md:grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                        {treatmentTypes.map((type) => (
                          <div key={type} className="flex items-center space-x-2">
                            <Checkbox
                              id={`treatment-${type}`}
                              checked={formData.treatmentTypes.includes(type)}
                              onCheckedChange={(checked) => handleArrayChange('treatmentTypes', type, checked)}
                            />
                            <Label htmlFor={`treatment-${type}`} className="text-sm cursor-pointer">
                              {type}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {errors.treatmentTypes && <p className="text-red-500 text-sm mt-1">{errors.treatmentTypes}</p>}
                    </div>

                    <div>
                      <Label>Specializations</Label>
                      <div className="grid md:grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
                        {specializations.slice(0, 10).map((spec) => (
                          <div key={spec} className="flex items-center space-x-2">
                            <Checkbox
                              id={`spec-${spec}`}
                              checked={formData.specializations.includes(spec)}
                              onCheckedChange={(checked) => handleArrayChange('specializations', spec, checked)}
                            />
                            <Label htmlFor={`spec-${spec}`} className="text-sm cursor-pointer">
                              {spec}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="accreditation">Accreditations (separate by commas)</Label>
                      <Input
                        id="accreditation"
                        value={formData.accreditation}
                        onChange={(e) => handleInputChange('accreditation', e.target.value)}
                        placeholder="e.g., Joint Commission, CARF, NAATP"
                      />
                    </div>
                  </div>
                )}

                {/* Step 5: Review & Submit */}
                {step === 5 && (
                  <div className="space-y-6">
                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        Please review your information carefully. Once submitted, your facility listing 
                        will be reviewed by our team within 2-3 business days.
                      </AlertDescription>
                    </Alert>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-3">Facility Summary</h4>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <strong>Facility:</strong> {formData.facilityName}<br />
                          <strong>Contact:</strong> {formData.contactPerson}<br />
                          <strong>Phone:</strong> {formData.phone}<br />
                          <strong>Email:</strong> {formData.email}
                        </div>
                        <div>
                          <strong>Address:</strong> {formData.address}<br />
                          <strong>City:</strong> {formData.city}, {formData.state} {formData.zipCode}<br />
                          <strong>Website:</strong> {formData.website}<br />
                          <strong>Established:</strong> {formData.established}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="agreeToTerms"
                          checked={formData.agreeToTerms}
                          onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked)}
                          className={errors.agreeToTerms ? 'border-red-500' : ''}
                        />
                        <Label htmlFor="agreeToTerms" className="text-sm cursor-pointer">
                          I agree to the{' '}
                          <Link to="/terms" className="text-blue-600 hover:underline">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" className="text-blue-600 hover:underline">
                            Privacy Policy
                          </Link>
                        </Label>
                      </div>
                      {errors.agreeToTerms && <p className="text-red-500 text-sm">{errors.agreeToTerms}</p>}

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="agreeToReview"
                          checked={formData.agreeToReview}
                          onCheckedChange={(checked) => handleInputChange('agreeToReview', checked)}
                          className={errors.agreeToReview ? 'border-red-500' : ''}
                        />
                        <Label htmlFor="agreeToReview" className="text-sm cursor-pointer">
                          I understand that my facility submission will be manually reviewed and 
                          I may be contacted for additional information or verification.
                        </Label>
                      </div>
                      {errors.agreeToReview && <p className="text-red-500 text-sm">{errors.agreeToReview}</p>}
                    </div>
                  </div>
                )}

                {submitError && (
                  <Alert className="border-red-200 bg-red-50 mt-6">
                    <AlertDescription className="text-red-700">{submitError}</AlertDescription>
                  </Alert>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-8 border-t">
                  <div>
                    {step > 1 && (
                      <Button type="button" variant="outline" onClick={handlePrevious}>
                        Previous
                      </Button>
                    )}
                  </div>

                  <div>
                    {step < 5 ? (
                      <Button type="button" onClick={handleNext}>
                        Next
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="bg-gradient-to-r from-blue-600 to-purple-600"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                        <CheckCircle className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Already have an account */}
          <div className="text-center mt-8">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/facility-login" className="text-blue-600 hover:underline font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitySignupPage;